// Master-tab election over the Web Locks API (docs/01-architecture.md §6,
// docs/06-clients.md §4). Exactly one tab — the holder of the exclusive
// "merkury-master" lock — owns the WSS connection and rings for incoming
// calls; every other tab is a follower showing "active in another tab" with a
// take-over button. The browser releases the lock automatically when the
// leader closes or crashes, so leadership transfers with zero coordination
// code on the unload path. Take-over uses the spec's `steal` option: the old
// leader's lock request rejects with an AbortError, which we treat as an
// orderly demotion.
//
// All platform surfaces (locks, BroadcastChannel) are injected so the
// election is fully unit-testable without a browser.

import { Store, type ReadableStore } from '@merkury/brain/call/store.js';

export const MASTER_LOCK_NAME = 'merkury-master';
export const TABS_CHANNEL_NAME = 'merkury-tabs';

// --- Injected platform surfaces (subsets of the real Web APIs) --------------

export interface LockLike {
  readonly name: string;
}

export interface LockRequestOptions {
  ifAvailable?: boolean;
  steal?: boolean;
  signal?: AbortSignal;
}

/** Structural subset of `navigator.locks`. */
export interface LockManagerLike {
  request(
    name: string,
    options: LockRequestOptions,
    callback: (lock: LockLike | null) => Promise<unknown>,
  ): Promise<unknown>;
}

/** Structural subset of `BroadcastChannel`. */
export interface BroadcastChannelLike {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

// --- Published state ---------------------------------------------------------

export type TabRole =
  | 'pending' // start() called, first lock probe not resolved yet
  | 'leader' // holds the lock: owns WSS + ringing
  | 'follower'; // another tab holds the lock

export interface TabElectionState {
  role: TabRole;
  /** Last state summary broadcast by the leader (followers' UI/reconciliation). */
  leaderState: unknown;
  /** True between takeOver() and the lock actually being granted. */
  takingOver: boolean;
}

const INITIAL_STATE: TabElectionState = { role: 'pending', leaderState: null, takingOver: false };

// --- Cross-tab channel messages ----------------------------------------------

type TabsMessage =
  | { kind: 'leader-state'; state: unknown }
  | { kind: 'sync-request' };

function isTabsMessage(data: unknown): data is TabsMessage {
  if (typeof data !== 'object' || data === null) return false;
  const kind = (data as { kind?: unknown }).kind;
  return kind === 'leader-state' || kind === 'sync-request';
}

// --- Election ----------------------------------------------------------------

export interface TabElectionDeps {
  /** `navigator.locks`; absent in some older WebViews/private modes, in which
   *  case no cross-tab contention is possible and this tab leads outright. */
  locks: LockManagerLike | undefined;
  /** Defaults to `new BroadcastChannel(TABS_CHANNEL_NAME)`. */
  channel?: BroadcastChannelLike;
  /** Called when this tab becomes leader (start the engine / WSS). */
  onLead?: () => void;
  /** Called when leadership is taken away (stop the engine, close WSS). */
  onDemote?: () => void;
  /** Leader-side: current state summary to answer followers' sync requests. */
  getLeaderState?: () => unknown;
}

export class TabElection {
  readonly state: ReadableStore<TabElectionState> & Store<TabElectionState>;

  private readonly deps: TabElectionDeps;
  private channel: BroadcastChannelLike | undefined;
  private stopped = false;
  private started = false;
  /** Resolves the leader-session callback promise (releases the lock). */
  private releaseLock: (() => void) | undefined;
  /** Aborts the queued waiting request (used before a steal and on stop()). */
  private waitAbort: AbortController | undefined;

  constructor(deps: TabElectionDeps) {
    this.deps = deps;
    this.state = new Store<TabElectionState>(INITIAL_STATE);
  }

  /** Join the election. Resolves once the initial role is known. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    // The real BroadcastChannel is a structural superset of our interface
    // (its onmessage takes a full MessageEvent; we only read .data).
    const channel: BroadcastChannelLike =
      this.deps.channel ?? (new BroadcastChannel(TABS_CHANNEL_NAME) as BroadcastChannelLike);
    this.channel = channel;
    channel.onmessage = (ev) => this.handleMessage(ev.data);

    // No Web Locks API → no way for tabs to contend, and a crashed election
    // would mean nobody rings (prime directive): lead unconditionally.
    if (!this.deps.locks) {
      this.becomeLeader();
      return;
    }

    // Probe without waiting: grabs the lock if free (→ leader), otherwise
    // tells us immediately that another tab leads (→ follower).
    let roleKnown: () => void = () => {};
    const initialRole = new Promise<void>((resolve) => {
      roleKnown = resolve;
    });
    this.requestLeadership({ ifAvailable: true }, (lock) => {
      if (lock === null) this.becomeFollower();
      roleKnown();
    });
    await initialRole;

    if (this.state.get().role === 'follower') this.enqueueWaitingRequest();
  }

  /** Follower action: steal the lock from the current leader. */
  takeOver(): void {
    if (this.stopped || this.state.get().role !== 'follower') return;
    this.state.patch({ takingOver: true });
    // Drop our queued request first so the steal doesn't leave a duplicate
    // request of ours in the lock queue.
    this.waitAbort?.abort();
    this.waitAbort = undefined;
    this.requestLeadership({ steal: true });
  }

  /** Leader-side: broadcast a state summary to follower tabs. */
  publishLeaderState(state: unknown): void {
    if (this.state.get().role !== 'leader') return;
    this.channel?.postMessage({ kind: 'leader-state', state } satisfies TabsMessage);
  }

  /** Follower-side: ask the leader to re-broadcast its state (visibilitychange). */
  requestSync(): void {
    if (this.state.get().role !== 'follower') return;
    this.channel?.postMessage({ kind: 'sync-request' } satisfies TabsMessage);
  }

  /** Leave the election: release the lock (if held) and stop listening. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.waitAbort?.abort();
    this.waitAbort = undefined;
    this.releaseLock?.();
    this.releaseLock = undefined;
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel.close();
      this.channel = undefined;
    }
  }

  // --- internals -------------------------------------------------------------

  private holdWhileLeader(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.releaseLock = resolve;
    });
  }

  /** Queue a plain waiting request: granted when the leader's lock releases. */
  private enqueueWaitingRequest(): void {
    if (this.stopped) return;
    const abort = new AbortController();
    this.waitAbort = abort;
    this.requestLeadership({ signal: abort.signal }, () => {
      if (this.waitAbort === abort) this.waitAbort = undefined;
    });
  }

  /**
   * Issue a lock request that, when granted, makes this tab the leader and
   * holds the lock until stop() or a steal. The rejection handler only
   * demotes if *this* request had actually been granted — a deliberately
   * aborted waiting request must never demote a freshly-stolen leadership.
   */
  private requestLeadership(
    options: LockRequestOptions,
    onSettledGrant?: (lock: LockLike | null) => void,
  ): void {
    const locks = this.deps.locks;
    if (!locks) return;
    let granted = false;
    void locks
      .request(MASTER_LOCK_NAME, options, (lock) => {
        onSettledGrant?.(lock);
        if (lock === null || this.stopped) return Promise.resolve();
        granted = true;
        this.becomeLeader();
        return this.holdWhileLeader();
      })
      .catch(() => {
        // AbortError: either our own abort of a waiting request (granted ===
        // false → ignore) or our held lock was stolen (granted === true).
        if (granted) this.handleStolen();
      });
  }

  /** Our held lock was stolen: demote and requeue for a future transfer. */
  private handleStolen(): void {
    if (this.stopped) return;
    // Settle the abandoned holder callback so its promise doesn't dangle.
    this.releaseLock?.();
    this.releaseLock = undefined;
    if (this.state.get().role === 'leader') {
      this.becomeFollower();
      this.deps.onDemote?.();
      this.enqueueWaitingRequest();
    }
  }

  private becomeLeader(): void {
    this.state.patch({ role: 'leader', takingOver: false });
    this.deps.onLead?.();
    // Announce so follower UIs update without waiting for a sync request.
    this.publishLeaderState(this.deps.getLeaderState?.() ?? null);
  }

  private becomeFollower(): void {
    this.state.patch({ role: 'follower', takingOver: false });
  }

  private handleMessage(data: unknown): void {
    if (this.stopped || !isTabsMessage(data)) return;
    const role = this.state.get().role;
    if (data.kind === 'leader-state' && role !== 'leader') {
      this.state.patch({ leaderState: data.state });
    } else if (data.kind === 'sync-request' && role === 'leader') {
      this.publishLeaderState(this.deps.getLeaderState?.() ?? null);
    }
  }
}
